import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, AlertTriangle, CheckCircle2, Loader2, PenLine } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { SignaturePad } from '../SignaturePad';
import { resolveCountryName } from '@/utils/countryResolver';

// Art. 36, para. 2 categories - Political positions (IDs stored in English in backend)
const PARA_2_CATEGORY_IDS = [
  'heads_of_state',
  'parliament_members',
  'supreme_court',
  'court_of_auditors',
  'central_bank',
  'ambassadors',
  'military_officers',
  'state_enterprises',
  'municipal_officials',
  'political_parties',
  'international_org',
];

// Art. 36, para. 5 categories - Family/Associates (IDs stored in English in backend)
const PARA_5_CATEGORY_IDS = [
  'spouse',
  'descendants',
  'ascendants',
  'relatives',
  'joint_beneficial',
  'sole_beneficial',
];

interface PEPDeclarationFormProps {
  onBack: () => void;
  onComplete: () => void;
  personName?: string;
  personType?: string;
}

export function PEPDeclarationForm({ onBack, onComplete, personName, personType }: PEPDeclarationFormProps) {
  const { t } = useTranslation();
  const { data, updateData } = useOnboarding();
  
  // Current Para 2 status - categories selected directly
  const [selectedPara2Categories, setSelectedPara2Categories] = useState<string[]>([]);
  const [notUnderPara2, setNotUnderPara2] = useState(false);
  
  // Last 12 months Para 2 status
  const [belongedPara2Last12, setBelongedPara2Last12] = useState(false);
  const [selectedPara2Last12Categories, setSelectedPara2Last12Categories] = useState<string[]>([]);
  const [notBelongedPara2Last12, setNotBelongedPara2Last12] = useState(false);
  
  // Current Para 5 status - categories selected directly
  const [selectedPara5Categories, setSelectedPara5Categories] = useState<string[]>([]);
  const [notUnderPara5, setNotUnderPara5] = useState(false);
  
  // Last 12 months Para 5 status
  const [belongedPara5Last12, setBelongedPara5Last12] = useState(false);
  const [selectedPara5Last12Categories, setSelectedPara5Last12Categories] = useState<string[]>([]);
  const [notBelongedPara5Last12, setNotBelongedPara5Last12] = useState(false);
  
  // Additional information
  const [additionalInfo, setAdditionalInfo] = useState('');
  
  // Signature state
  const [signature, setSignature] = useState<string | null>(null);
  
  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Refs for smooth scrolling to validation errors
  const para2CurrentRef = useRef<HTMLDivElement>(null);
  const para2Last12Ref = useRef<HTMLDivElement>(null);
  const para5CurrentRef = useRef<HTMLDivElement>(null);
  const para5Last12Ref = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<HTMLDivElement>(null);

  // Validation checks for each section
  const validationState = {
    para2Current: selectedPara2Categories.length > 0 || notUnderPara2,
    para2Last12: belongedPara2Last12 ? selectedPara2Last12Categories.length > 0 : notBelongedPara2Last12,
    para5Current: selectedPara5Categories.length > 0 || notUnderPara5,
    para5Last12: belongedPara5Last12 ? selectedPara5Last12Categories.length > 0 : notBelongedPara5Last12,
    signature: !!signature,
  };

  const incompleteCount = Object.values(validationState).filter(v => !v).length;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Format date for display — avoids UTC timezone shift by parsing components directly
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    // Split YYYY-MM-DD to avoid timezone shift from new Date() parsing
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
    }
    return dateStr;
  };

  // Pre-filled data from document verification and earlier steps
  const fullName = personName 
    ? personName
    : data.firstName && data.lastName 
      ? `${data.firstName} ${data.lastName}` 
      : data.extractedFirstName && data.extractedLastName
        ? `${data.extractedFirstName} ${data.extractedLastName}`
        : '[Name from document]';
  
  const documentNumber = data.extractedDocumentNumber || '[Document number]';
  const documentExpiry = data.extractedDocumentExpiry ? formatDate(data.extractedDocumentExpiry) : '[Expiry date]';
  const documentCountry = resolveCountryName(data.extractedDocumentCountry) || '[Issuing country]';
  const nationality = resolveCountryName(data.extractedNationality || data.countryOfResidence) || '[Nationality]';
  const dateOfBirth = data.dateOfBirth 
    ? formatDate(data.dateOfBirth) 
    : data.extractedDateOfBirth 
      ? formatDate(data.extractedDateOfBirth)
      : '[Date of birth]';
  
  // Address
  const addressData = data.address.street 
    ? data.address 
    : data.extractedAddress;
  const address = addressData?.street 
    ? `${addressData.street}, ${addressData.city || ''}, ${addressData.postalCode || ''}, ${resolveCountryName(addressData.country) || ''}`
    : '[Address]';

  // Handle Para 2 category selection
  const handlePara2CategoryToggle = (categoryId: string) => {
    const newSelected = selectedPara2Categories.includes(categoryId) 
      ? selectedPara2Categories.filter(id => id !== categoryId)
      : [...selectedPara2Categories, categoryId];
    
    setSelectedPara2Categories(newSelected);
    if (newSelected.length > 0) {
      setNotUnderPara2(false);
    }
  };

  const handleNotUnderPara2Toggle = (checked: boolean) => {
    setNotUnderPara2(checked);
    if (checked) {
      setSelectedPara2Categories([]);
    }
  };

  const handlePara2Last12CategoryToggle = (categoryId: string) => {
    const newSelected = selectedPara2Last12Categories.includes(categoryId) 
      ? selectedPara2Last12Categories.filter(id => id !== categoryId)
      : [...selectedPara2Last12Categories, categoryId];
    
    setSelectedPara2Last12Categories(newSelected);
    setBelongedPara2Last12(newSelected.length > 0);
    if (newSelected.length > 0) {
      setNotBelongedPara2Last12(false);
    }
  };

  const handleNotBelongedPara2Last12Toggle = (checked: boolean) => {
    setNotBelongedPara2Last12(checked);
    if (checked) {
      setSelectedPara2Last12Categories([]);
      setBelongedPara2Last12(false);
    }
  };

  const handlePara5CategoryToggle = (categoryId: string) => {
    const newSelected = selectedPara5Categories.includes(categoryId) 
      ? selectedPara5Categories.filter(id => id !== categoryId)
      : [...selectedPara5Categories, categoryId];
    
    setSelectedPara5Categories(newSelected);
    if (newSelected.length > 0) {
      setNotUnderPara5(false);
    }
  };

  const handleNotUnderPara5Toggle = (checked: boolean) => {
    setNotUnderPara5(checked);
    if (checked) {
      setSelectedPara5Categories([]);
    }
  };

  const handlePara5Last12CategoryToggle = (categoryId: string) => {
    const newSelected = selectedPara5Last12Categories.includes(categoryId) 
      ? selectedPara5Last12Categories.filter(id => id !== categoryId)
      : [...selectedPara5Last12Categories, categoryId];
    
    setSelectedPara5Last12Categories(newSelected);
    setBelongedPara5Last12(newSelected.length > 0);
    if (newSelected.length > 0) {
      setNotBelongedPara5Last12(false);
    }
  };

  const handleNotBelongedPara5Last12Toggle = (checked: boolean) => {
    setNotBelongedPara5Last12(checked);
    if (checked) {
      setSelectedPara5Last12Categories([]);
      setBelongedPara5Last12(false);
    }
  };

  const fallsUnderPara2 = selectedPara2Categories.length > 0;
  const fallsUnderPara5 = selectedPara5Categories.length > 0;

  const isValid = () => {
    if (selectedPara2Categories.length === 0 && !notUnderPara2) return false;
    if (!belongedPara2Last12 && !notBelongedPara2Last12) return false;
    if (belongedPara2Last12 && selectedPara2Last12Categories.length === 0) return false;
    if (selectedPara5Categories.length === 0 && !notUnderPara5) return false;
    if (!belongedPara5Last12 && !notBelongedPara5Last12) return false;
    if (belongedPara5Last12 && selectedPara5Last12Categories.length === 0) return false;
    if (!signature) return false;
    return true;
  };

  const scrollToFirstIncomplete = () => {
    const sectionRefs = [
      { ref: para2CurrentRef, valid: validationState.para2Current },
      { ref: para2Last12Ref, valid: validationState.para2Last12 },
      { ref: para5CurrentRef, valid: validationState.para5Current },
      { ref: para5Last12Ref, valid: validationState.para5Last12 },
      { ref: signatureRef, valid: validationState.signature },
    ];

    const firstIncomplete = sectionRefs.find(section => !section.valid);
    if (firstIncomplete?.ref.current) {
      firstIncomplete.ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      setShowValidation(true);
      setTimeout(() => scrollToFirstIncomplete(), 100);
      return;
    }

    const isPEP = fallsUnderPara2 || fallsUnderPara5 || belongedPara2Last12 || belongedPara5Last12;
    
    // Store all data in English for backend
    const pepDeclaration = {
      fallsUnderPara2,
      para2Categories: selectedPara2Categories, // English IDs
      belongedPara2Last12Months: belongedPara2Last12,
      para2Last12MonthsCategories: selectedPara2Last12Categories, // English IDs
      fallsUnderPara5,
      para5Categories: selectedPara5Categories, // English IDs
      belongedPara5Last12Months: belongedPara5Last12,
      para5Last12MonthsCategories: selectedPara5Last12Categories, // English IDs
      additionalInformation: additionalInfo,
      declarationDate: new Date().toISOString(),
    };

    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      updateData({
        isPEP,
        pepDetails: JSON.stringify({
          ...pepDeclaration,
          signature: signature,
          signedVia: 'in-app',
          signedAt: new Date().toISOString(),
        }),
      });

      toast({
        title: t('pep.declaration.signed'),
        description: t('pep.declaration.signedDesc'),
      });

      onComplete();
    } catch (error) {
      console.error('Signing error:', error);
      toast({
        title: t('pep.declaration.signingFailed'),
        description: error instanceof Error ? error.message : t('common.error'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-foreground">{t('pep.declaration.title')}</h1>
            <p className="text-xs text-muted-foreground">{t('pep.declaration.subtitle')}</p>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="px-4 pt-2 pb-6 max-w-2xl mx-auto">
          {/* Declaration Header */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold tracking-wide text-foreground mb-2">{t('pep.declaration.headerTitle')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('pep.declaration.headerSubtitle')}
            </p>
          </div>

          {/* Personal Information Section */}
          <div className="bg-muted/30 rounded-xl p-4 mb-6 border border-border">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-accent" />
              <span className="text-xs font-medium text-accent">{t('pep.declaration.prefilled')}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{t('pep.declaration.undersigned')}</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pep.declaration.fullName')}:</span>
                <span className="font-medium text-foreground">{fullName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pep.declaration.dateOfBirth')}:</span>
                <span className="font-medium text-foreground">{dateOfBirth}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pep.declaration.documentNo')}:</span>
                <span className="font-medium text-foreground">{documentNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pep.declaration.citizenship')}:</span>
                <span className="font-medium text-foreground">{nationality}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pep.declaration.documentValidity')}:</span>
                <span className="font-medium text-foreground">{documentExpiry}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pep.declaration.issuingCountry')}:</span>
                <span className="font-medium text-foreground">{documentCountry}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">{t('pep.declaration.address')}:</span>
                <span className="font-medium text-foreground">{address}</span>
              </div>
            </div>
          </div>

          {/* Declare Statement */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-6 py-2 text-lg font-bold tracking-[0.3em] text-accent uppercase">
                {t('pep.declaration.declare')}
              </span>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-2">{t('pep.declaration.declareSubtext')}</p>
          </div>

          {/* Section 1: Art. 36, para. 2 - Current Status */}
          <div ref={para2CurrentRef} className="mb-6">
            <div className="flex items-start justify-between gap-2 mb-4">
              <p className="text-sm font-bold text-foreground text-justify">
                {t('pep.para2.currentTitle')}
              </p>
              {showValidation && !validationState.para2Current && (
                <span className="flex-shrink-0 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                  {t('common.required')}
                </span>
              )}
            </div>

            <div className={`bg-muted/20 rounded-xl border p-4 space-y-3 transition-colors ${
              showValidation && !validationState.para2Current ? 'border-destructive' : 'border-border'
            }`}>
              {PARA_2_CATEGORY_IDS.map((categoryId) => (
                <button
                  key={categoryId}
                  onClick={() => !notUnderPara2 && handlePara2CategoryToggle(categoryId)}
                  disabled={notUnderPara2}
                  className={`w-full flex items-start gap-3 text-left transition-opacity ${notUnderPara2 ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    selectedPara2Categories.includes(categoryId) ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {selectedPara2Categories.includes(categoryId) && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed capitalize text-justify">
                    {t(`pep.para2.categories.${categoryId}`)}
                  </p>
                </button>
              ))}

              <div className="pt-3 border-t border-border">
                <button
                  onClick={() => handleNotUnderPara2Toggle(!notUnderPara2)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    notUnderPara2 ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {notUnderPara2 && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground text-justify">
                    {t('pep.para2.notUnder')}
                  </p>
                </button>
              </div>
            </div>
          </div>

          {/* Section 2: Art. 36, para. 2 - Last 12 Months */}
          <div ref={para2Last12Ref} className="mb-6">
            <div className="flex items-start justify-between gap-2 mb-4">
              <p className="text-sm font-bold text-foreground text-justify">
                {t('pep.para2.last12Title')}
              </p>
              {showValidation && !validationState.para2Last12 && (
                <span className="flex-shrink-0 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                  {t('common.required')}
                </span>
              )}
            </div>

            <div className={`bg-muted/20 rounded-xl border p-4 space-y-3 transition-colors ${
              showValidation && !validationState.para2Last12 ? 'border-destructive' : 'border-border'
            }`}>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setBelongedPara2Last12(true);
                    setNotBelongedPara2Last12(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors border border-border hover:bg-muted/40"
                  style={{
                    backgroundColor: belongedPara2Last12 ? 'hsl(var(--accent))' : undefined,
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    belongedPara2Last12 ? 'bg-accent-foreground/20' : 'bg-muted/30 border border-border'
                  }`}>
                    {belongedPara2Last12 && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${belongedPara2Last12 ? 'text-accent-foreground' : 'text-foreground'}`}>
                    {t('common.yes')}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setNotBelongedPara2Last12(true);
                    setBelongedPara2Last12(false);
                    setSelectedPara2Last12Categories([]);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors border border-border hover:bg-muted/40"
                  style={{
                    backgroundColor: notBelongedPara2Last12 ? 'hsl(var(--accent))' : undefined,
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    notBelongedPara2Last12 ? 'bg-accent-foreground/20' : 'bg-muted/30 border border-border'
                  }`}>
                    {notBelongedPara2Last12 && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${notBelongedPara2Last12 ? 'text-accent-foreground' : 'text-foreground'}`}>
                    {t('common.no')}
                  </span>
                </button>
              </div>

              {belongedPara2Last12 && (
                <div className="pt-3 border-t border-border space-y-2">
                  <p className="text-sm text-muted-foreground">{t('pep.declaration.additionalInfoPlaceholder')}</p>
                  <Textarea
                    value={selectedPara2Last12Categories.join(', ')}
                    onChange={(e) => setSelectedPara2Last12Categories(e.target.value ? [e.target.value] : [])}
                    placeholder={t('pep.declaration.additionalInfoPlaceholder')}
                    className="min-h-[80px] bg-background"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Art. 36, para. 5 - Current Status */}
          <div ref={para5CurrentRef} className="mb-6">
            <div className="flex items-start justify-between gap-2 mb-4">
              <p className="text-sm font-bold text-foreground text-justify">
                {t('pep.para5.currentTitle')}
              </p>
              {showValidation && !validationState.para5Current && (
                <span className="flex-shrink-0 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                  {t('common.required')}
                </span>
              )}
            </div>

            <div className={`bg-muted/20 rounded-xl border p-4 space-y-3 transition-colors ${
              showValidation && !validationState.para5Current ? 'border-destructive' : 'border-border'
            }`}>
              {PARA_5_CATEGORY_IDS.map((categoryId) => (
                <button
                  key={categoryId}
                  onClick={() => !notUnderPara5 && handlePara5CategoryToggle(categoryId)}
                  disabled={notUnderPara5}
                  className={`w-full flex items-start gap-3 text-left transition-opacity ${notUnderPara5 ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    selectedPara5Categories.includes(categoryId) ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {selectedPara5Categories.includes(categoryId) && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed capitalize text-justify">
                    {t(`pep.para5.categories.${categoryId}`)}
                  </p>
                </button>
              ))}

              <div className="pt-3 border-t border-border">
                <button
                  onClick={() => handleNotUnderPara5Toggle(!notUnderPara5)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    notUnderPara5 ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {notUnderPara5 && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground text-justify">
                    {t('pep.para5.notUnder')}
                  </p>
                </button>
              </div>
            </div>
          </div>

          {/* Section 4: Art. 36, para. 5 - Last 12 Months */}
          <div ref={para5Last12Ref} className="mb-6">
            <div className="flex items-start justify-between gap-2 mb-4">
              <p className="text-sm font-bold text-foreground text-justify">
                {t('pep.para5.last12Title')}
              </p>
              {showValidation && !validationState.para5Last12 && (
                <span className="flex-shrink-0 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                  {t('common.required')}
                </span>
              )}
            </div>

            <div className={`bg-muted/20 rounded-xl border p-4 space-y-3 transition-colors ${
              showValidation && !validationState.para5Last12 ? 'border-destructive' : 'border-border'
            }`}>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setBelongedPara5Last12(true);
                    setNotBelongedPara5Last12(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors border border-border hover:bg-muted/40"
                  style={{
                    backgroundColor: belongedPara5Last12 ? 'hsl(var(--accent))' : undefined,
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    belongedPara5Last12 ? 'bg-accent-foreground/20' : 'bg-muted/30 border border-border'
                  }`}>
                    {belongedPara5Last12 && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${belongedPara5Last12 ? 'text-accent-foreground' : 'text-foreground'}`}>
                    {t('common.yes')}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setNotBelongedPara5Last12(true);
                    setBelongedPara5Last12(false);
                    setSelectedPara5Last12Categories([]);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors border border-border hover:bg-muted/40"
                  style={{
                    backgroundColor: notBelongedPara5Last12 ? 'hsl(var(--accent))' : undefined,
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    notBelongedPara5Last12 ? 'bg-accent-foreground/20' : 'bg-muted/30 border border-border'
                  }`}>
                    {notBelongedPara5Last12 && (
                      <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${notBelongedPara5Last12 ? 'text-accent-foreground' : 'text-foreground'}`}>
                    {t('common.no')}
                  </span>
                </button>
              </div>

              {belongedPara5Last12 && (
                <div className="pt-3 border-t border-border space-y-2">
                  <p className="text-sm text-muted-foreground">{t('pep.declaration.additionalInfoPlaceholder')}</p>
                  <Textarea
                    value={selectedPara5Last12Categories.join(', ')}
                    onChange={(e) => setSelectedPara5Last12Categories(e.target.value ? [e.target.value] : [])}
                    placeholder={t('pep.declaration.additionalInfoPlaceholder')}
                    className="min-h-[80px] bg-background"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Declaration Footer */}
          <div className="bg-muted/30 rounded-xl p-4 mb-6 border border-border">
            <div className="flex justify-between text-sm">
              <div>
                <span className="text-muted-foreground">{t('finalDeclarations.date')}:</span>
                <p className="font-medium text-foreground mt-1">
                  {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              </div>
              <div className="text-right">
                <span className="text-muted-foreground">{t('finalDeclarations.applicant')}:</span>
                <p className="font-medium text-foreground mt-1">{fullName}</p>
              </div>
            </div>
          </div>

          {/* Electronic Signature Pad */}
          <div ref={signatureRef} className={`bg-muted/50 rounded-xl p-4 mb-6 border transition-colors ${
            showValidation && !validationState.signature ? 'border-destructive' : 'border-border'
          }`}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <PenLine className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('pep.declaration.signature')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('pep.declaration.signatureHint')}
                  </p>
                </div>
              </div>
              {showValidation && !validationState.signature && (
                <span className="flex-shrink-0 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                  {t('common.required')}
                </span>
              )}
            </div>
            <SignaturePad onSignatureChange={setSignature} />
          </div>
        </div>
      </ScrollArea>

      {/* Submit Button */}
      <div className="sticky bottom-0 bg-background border-t border-border p-4">
        {showValidation && incompleteCount > 0 && (
          <div className="flex items-center gap-2 mb-3 p-3 bg-destructive/10 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">
              {t('pep.declaration.incompleteForm')}
            </p>
          </div>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {t('pep.declaration.signing')}
            </>
          ) : (
            t('pep.declaration.signAndSubmit')
          )}
        </Button>
      </div>
    </div>
  );
}
